import React, { useEffect, useState, useRef } from 'react';
import './benchmark_perfis_sap.css';
// Header provided by AppLayout
import axios from 'axios';
import DataTable from './DataTable';
import { ActionMenu } from './DataTable';
import { DataTableColumn } from './DataTable';
import { Icons } from './ActionIcons';
import ProgressBar from './ProgressBar';

const DEFAULT_PROMPT = `Classifique as transações/APPs listados atribuindo para cada item: Processo > Subprocesso > Atividade. Para cada item também informe 'purpose' (finalidade) e 'justification' (uma breve justificativa do mapeamento). Responda apenas em JSON no formato [{ "code": "<code>", "process": "...", "subprocess": "...", "activity": "...", "purpose": "...", "justification": "..." }].`;

type Item = {
  _id?: string;
  code: string;
  description?: string;
  process?: string;
  subprocess?: string;
  activity?: string;
  type?: string;
  purpose?: string;
  justification?: string;
};

const BenchmarkPerfisSAP: React.FC = () => {
  // theme handled globally in AppLayout
  const theme = (typeof window !== 'undefined' ? (localStorage.getItem('phoenixTheme') || 'light') : 'light');
  
  // generic fetch that accepts optional process/subprocess/activity and returns matching docs (pages through API)
  const fetchItemsForFilter = async (processName?: string, subprocessName?: string, activityName?: string) => {
    try {
      setActivityLoading(true);
      setActivityItems([]);
      setSelectedFilter({ process: processName, subprocess: subprocessName, activity: activityName });
      const base = process.env.REACT_APP_API_URL || '';
  const urlBase = base ? `${base.replace(/\/$/, '')}/api/sap-transactions` : '/api/sap-transactions';
  // Prefer server-side exact-match filters supported by backend
  const params: any = { page: 1, pageSize: 1000, sortBy: 'code', sortDir: 'asc' };
  if (processName) params.process = processName;
  if (subprocessName) params.subprocess = subprocessName;
  if (activityName) params.activity = activityName;
  const res = await axios.get(urlBase, { params, withCredentials: true });
  const fetched: Item[] = res.data.items || [];
  setActivityItems(fetched);
  // also update main table so filtered results are visible to the user
  setItems(fetched);
  setTotal(typeof res.data.total === 'number' ? res.data.total : fetched.length);
  setPage(1);
  setPageSize(params.pageSize);
    } catch (err) {
      console.error('Erro fetch items filter', err);
      setActivityItems([]);
    } finally {
      setActivityLoading(false);
    }
  };

  // send a message to the backend chat proxy and append to chatMessages
  const sendChatMessage = async () => {
    const text = (chatInput || '').trim();
    if (!text) return;
    let url = '';
    try {
      setChatLoading(true);
      // append user message locally
      setChatMessages(prev => [...prev, { role: 'user', content: text }]);
      setChatInput('');
      const base = process.env.REACT_APP_API_URL || '';
      url = base ? `${base.replace(/\/$/, '')}/api/ai/chat` : '/api/ai/chat';
      // send conversation to backend
      const payload = { messages: [...chatMessages, { role: 'user', content: text }], model };
      // log raw sent
      addLogEntry('sent', `Chat -> POST ${url}`, payload);
      const res = await axios.post(url, payload, { withCredentials: true });
      if (res && res.data && res.data.ok) {
        // log raw received (include usage if present)
        addLogEntry('received', `Chat <- response ${url}`, res.data, res.data.usage);
        const assistant = res.data.assistantText || (res.data.rawResponse && res.data.rawResponse.output && res.data.rawResponse.output[0] && res.data.rawResponse.output[0].content && res.data.rawResponse.output[0].content[0] && res.data.rawResponse.output[0].content[0].text) || '';
        if (assistant) setChatMessages(prev => [...prev, { role: 'assistant', content: assistant }]);
      } else {
        setChatMessages(prev => [...prev, { role: 'assistant', content: 'Erro: resposta inválida do servidor' }]);
      }
    } catch (err: any) {
      console.error('Erro chat IA', err);
  // log error (include usage if available in error payload)
  addLogEntry('received', `Chat <- error ${url}`, { message: err?.message || String(err), stack: err?.stack }, err?.response?.data?.usage || err?.response?.data?.raw?.usage);
      setChatMessages(prev => [...prev, { role: 'assistant', content: `Erro: ${err?.message || String(err)}` }]);
    } finally {
      setChatLoading(false);
    }
  };
  useEffect(() => { document.documentElement.setAttribute('data-bs-theme', theme); }, [theme]);

  const [items, setItems] = useState<Item[]>([]);
  const [allItems, setAllItems] = useState<Item[]>([]); // todos os dados carregados
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(Number(localStorage.getItem('benchmark_pageSize') || '25'));
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 });
  const loadingRef = useRef(false); // lock para evitar cargas simultâneas
  const [sortBy, setSortBy] = useState<string>('code');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('asc');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [model, setModel] = useState<string>(localStorage.getItem('benchmark_model') || 'gpt-3.5-turbo');
  const [batchSize, setBatchSize] = useState<number>(Number(localStorage.getItem('benchmark_batchSize') || '10'));
  const [temperature, setTemperature] = useState<number>(Number(localStorage.getItem('benchmark_temperature') || '0.2'));
  const [topP, setTopP] = useState<number>(Number(localStorage.getItem('benchmark_topP') || '1'));
  const [maxTokens, setMaxTokens] = useState<number>(Number(localStorage.getItem('benchmark_maxTokens') || '1500'));
  const [presencePenalty, setPresencePenalty] = useState<number>(Number(localStorage.getItem('benchmark_presencePenalty') || '0'));
  const [frequencyPenalty, setFrequencyPenalty] = useState<number>(Number(localStorage.getItem('benchmark_frequencyPenalty') || '0'));
  const [useWebSearch, setUseWebSearch] = useState<boolean>(localStorage.getItem('benchmark_useWebSearch') === 'true');
  const [classifyMenuOpen, setClassifyMenuOpen] = useState(false);
  const [contextMode, setContextMode] = useState<boolean>(localStorage.getItem('benchmark_contextMode') === 'true');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItemCode, setNewItemCode] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [newItemType, setNewItemType] = useState('TCODE');
  

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<Item | null>(null);
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [availableModels, setAvailableModels] = useState<string[] | null>(null);
  const [prompt, setPrompt] = useState(localStorage.getItem('benchmark_prompt') || DEFAULT_PROMPT);
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatMessages, setChatMessages] = useState<{ role: 'user'|'assistant'|'system'; content: string }[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  // IA log modal state and entries
  type LogEntry = { id: number; ts: string; direction: 'sent'|'received'; title: string; raw: any; usage?: any; expanded?: boolean };
  const [showLogModal, setShowLogModal] = useState(false);
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const nextLogIdRef = React.useRef<number>(1);
  const addLogEntry = (direction: 'sent'|'received', title: string, raw: any, usage?: any) => {
    const id = nextLogIdRef.current++;
    const entry: LogEntry = { id, ts: new Date().toLocaleString(), direction, title, raw, usage, expanded: false };
    setLogEntries(prev => [entry, ...prev]);
    return id;
  };
  const toggleLogEntry = (id: number) => setLogEntries(prev => prev.map(e => e.id === id ? { ...e, expanded: !e.expanded } : e));
  // indicadores (painel deslizante)
  const [showIndicators, setShowIndicators] = useState(false);
  const [drillState, setDrillState] = useState<{ level: 'process'|'subprocess'|'activity'; process?: string; subprocess?: string }>({ level: 'process' });
  const [allItemsForIndicators, setAllItemsForIndicators] = useState<Item[]>([]);
  const [indicatorsLoading, setIndicatorsLoading] = useState(false);
  const [indicatorsTree, setIndicatorsTree] = useState<any[] | null>(null);
  const [activityItems, setActivityItems] = useState<Item[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<{ process?: string; subprocess?: string; activity?: string } | null>(null);

  // agregações
  const getDataSource = () => (allItemsForIndicators && allItemsForIndicators.length > 0) ? allItemsForIndicators : items;

  const getProcessStats = () => {
    const ds = getDataSource();
    const map: Record<string, number> = {};
    ds.forEach(it => {
      const p = (it.process || '(Não classificado)').trim() || '(Não classificado)';
      map[p] = (map[p] || 0) + 1;
    });
    return Object.keys(map).map(k => ({ label: k, count: map[k] })).sort((a, b) => b.count - a.count);
  };
  const getSubprocessStats = (processKey?: string) => {
    const ds = getDataSource();
    const map: Record<string, number> = {};
    ds.forEach(it => {
      const p = (it.process || '(Não classificado)').trim() || '(Não classificado)';
      if (processKey && p !== processKey) return;
      const sp = (it.subprocess || '(Não classificado)').trim() || '(Não classificado)';
      map[sp] = (map[sp] || 0) + 1;
    });
    return Object.keys(map).map(k => ({ label: k, count: map[k] })).sort((a, b) => b.count - a.count);
  };
  const getActivityStats = (processKey?: string, subprocessKey?: string) => {
    const ds = getDataSource();
    const map: Record<string, number> = {};
    ds.forEach(it => {
      const p = (it.process || '(Não classificado)').trim() || '(Não classificado)';
      if (processKey && p !== processKey) return;
      const sp = (it.subprocess || '(Não classificado)').trim() || '(Não classificado)';
      if (subprocessKey && sp !== subprocessKey) return;
      const a = (it.activity || '(Não classificado)').trim() || '(Não classificado)';
      map[a] = (map[a] || 0) + 1;
    });
    return Object.keys(map).map(k => ({ label: k, count: map[k] })).sort((a, b) => b.count - a.count);
  };

  const handleBarClick = (label: string) => {
    if (drillState.level === 'process') {
      setDrillState({ level: 'subprocess', process: label });
      // fetch items for this process
      fetchItemsForFilter(label, undefined, undefined);
    } else if (drillState.level === 'subprocess') {
      setDrillState({ level: 'activity', process: drillState.process, subprocess: label });
      // fetch items for this process+subprocess
      if (drillState.process) fetchItemsForFilter(drillState.process, label, undefined);
    } else if (drillState.level === 'activity') {
      // clicking an activity shows items below
      if (drillState.process && drillState.subprocess) {
        fetchItemsForFilter(drillState.process, drillState.subprocess, label);
      }
    }
  };
  const goBackDrill = () => {
    if (drillState.level === 'activity') setDrillState({ level: 'subprocess', process: drillState.process });
    else if (drillState.level === 'subprocess') setDrillState({ level: 'process' });
  };

  // fetch all items from backend for indicators (ignores pagination)
  const fetchAllItemsForIndicators = async () => {
    // now use backend aggregation endpoint to fetch hierarchical counts
    try {
      setIndicatorsLoading(true);
      const base = process.env.REACT_APP_API_URL || '';
      const url = base ? `${base.replace(/\/$/, '')}/api/sap-transactions/indicators/tree` : '/api/sap-transactions/indicators/tree';
      const res = await axios.get(url, { withCredentials: true });
      if (res && res.data && res.data.ok) {
        setIndicatorsTree(res.data.tree || []);
        // do not populate fake items; we'll fetch real docs per selection
        setAllItemsForIndicators([]);
      } else {
        setIndicatorsTree([]);
        setAllItemsForIndicators([]);
      }
    } catch (err) {
      console.error('Erro ao buscar árvore de indicadores', err);
      setIndicatorsTree([]);
      setAllItemsForIndicators([]);
    } finally {
      setIndicatorsLoading(false);
    }
  };

  const [toast, setToast] = useState<{ message: string; type?: 'info'|'success'|'error'; visible: boolean }>({ message: '', type: 'info', visible: false });
  const [progressLines, setProgressLines] = useState<string[]>([]);
  const [progressActive, setProgressActive] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const classifyAbortRef = React.useRef<AbortController | null>(null);
  const uploadAbortRef = React.useRef<AbortController | null>(null);

  const showToast = (message: string, type: 'info'|'success'|'error' = 'info', timeout = 4500) => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), timeout);
  };

  const startProgress = (initialMessage?: string) => {
  setProgressLines(initialMessage ? [initialMessage] : []);
    setProgressActive(true);
  };
  const addProgress = (line: string) => {
    setProgressLines(prev => [...prev, line]);
  };
  const endProgress = (finalMessage?: string) => {
    if (finalMessage) addProgress(finalMessage);
    // hide after short delay so user can read
    setTimeout(() => {
      setProgressActive(false);
  setProgressLines([]);
  setProcessedCount(0);
  setTotalCount(0);
  if (classifyAbortRef.current) classifyAbortRef.current = null;
    }, 3000);
  };

  const [confirm, setConfirm] = useState<{ visible: boolean; message: string; onConfirm?: () => void }>({ visible: false, message: '' });
  const showConfirm = (message: string, onConfirm?: () => void) => setConfirm({ visible: true, message, onConfirm });
  const hideConfirm = () => setConfirm({ visible: false, message: '', onConfirm: undefined });

  useEffect(() => { localStorage.setItem('benchmark_prompt', prompt); }, [prompt]);

  // fetch available models from backend when parameters modal opens
  useEffect(() => {
    if (!showPromptEditor) return;
    const fetchModels = async () => {
      try {
        const base = process.env.REACT_APP_API_URL || '';
        const url = base ? `${base.replace(/\/$/, '')}/api/ai/models` : '/api/ai/models';
        const res = await axios.get(url, { withCredentials: true });
        if (res && res.data && res.data.ok && Array.isArray(res.data.models)) setAvailableModels(res.data.models);
      } catch (e) {
        console.warn('Não foi possível obter lista dinâmica de modelos', e);
        setAvailableModels(null);
      }
    };
    fetchModels();
  }, [showPromptEditor]);

  // Carregar todos os dados de uma vez
  const loadAllData = async () => {
    if (loadingRef.current) {
      console.log('Carga já em andamento, ignorando...');
      return; // evitar múltiplas cargas simultâneas
    }
    
    loadingRef.current = true;
    setLoading(true);
    setLoadingProgress({ current: 0, total: 0 });
    try {
      const base = process.env.REACT_APP_API_URL || '';
      const allData: Item[] = [];
      let currentPage = 1;
      const fetchPageSize = 5000; // carregar em blocos de 5000
      let totalItems = 0;
      
      // Primeira requisição para descobrir o total
      const firstUrl = base 
        ? `${base.replace(/\/$/, '')}/api/sap-transactions?page=1&pageSize=${fetchPageSize}&sortBy=${encodeURIComponent(sortBy)}&sortDir=${encodeURIComponent(sortDir)}` 
        : `/api/sap-transactions?page=1&pageSize=${fetchPageSize}&sortBy=${encodeURIComponent(sortBy)}&sortDir=${encodeURIComponent(sortDir)}`;
      
      const firstRes = await axios.get(firstUrl, { withCredentials: true });
      totalItems = firstRes.data.total || 0;
      allData.push(...(firstRes.data.items || []));
      setLoadingProgress(prev => ({ current: allData.length, total: totalItems }));
      console.log(`Total de itens no backend: ${totalItems}, primeira página carregada: ${allData.length} itens`);
      
      // Continuar carregando até ter todos os itens
      currentPage = 2;
      while (allData.length < totalItems) {
        const url = base 
          ? `${base.replace(/\/$/, '')}/api/sap-transactions?page=${currentPage}&pageSize=${fetchPageSize}&sortBy=${encodeURIComponent(sortBy)}&sortDir=${encodeURIComponent(sortDir)}` 
          : `/api/sap-transactions?page=${currentPage}&pageSize=${fetchPageSize}&sortBy=${encodeURIComponent(sortBy)}&sortDir=${encodeURIComponent(sortDir)}`;
        
        const res = await axios.get(url, { withCredentials: true });
        const items = res.data.items || [];
        
        if (items.length === 0) break; // sem mais dados
        
        allData.push(...items);
        const currentCount = allData.length;
        setLoadingProgress(prev => ({ current: currentCount, total: totalItems }));
        console.log(`Página ${currentPage}: ${currentCount}/${totalItems}`);
        currentPage++;
      }
      
      setAllItems(allData);
      setTotal(allData.length);
      loadingRef.current = false;
      console.log(`✓ Todos os dados carregados: ${allData.length} itens`);
    } catch (e) {
      console.error('Erro ao carregar todos os dados', e);
    } finally {
      setLoading(false);
      setLoadingProgress({ current: 0, total: 0 });
    }
  };

  // loadPage accepts optional pageSize override so callers can request a new page size
  const loadPage = async (p = 1, pageSizeOverride?: number, filtersOverride?: Record<string, string>) => {
    setLoading(true);
    try {
      const base = process.env.REACT_APP_API_URL || '';
      const ps = typeof pageSizeOverride === 'number' ? pageSizeOverride : pageSize;
      const filters = filtersOverride || columnFilters;
      
      // Construir query string com filtros
      let queryParams = `page=${p}&pageSize=${ps}&sortBy=${encodeURIComponent(sortBy)}&sortDir=${encodeURIComponent(sortDir)}`;
      
      // Adicionar filtros de coluna se existirem
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim()) {
          queryParams += `&${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        }
      });
      
  const url = base ? `${base.replace(/\/$/, '')}/api/sap-transactions?${queryParams}` : `/api/sap-transactions?${queryParams}`;
  const res = await axios.get(url, { withCredentials: true });
  setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setPage(res.data.page || p);
      // keep pageSize state consistent if override used
      if (typeof pageSizeOverride === 'number') setPageSize(pageSizeOverride);
    } catch (e) {
      console.error('Erro ao carregar transações', e);
    } finally { setLoading(false); }
  };

  useEffect(() => { 
    if (allItems.length === 0) {
      loadAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parseCsvText = (text: string) => {
    // supports either single column per line or semicolon-separated 3 columns: code;description;type
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    // detect header row
    const first = lines.length ? lines[0].split(';').map(p => p.trim()) : [];
    const hasHeader = first.some(c => /(code|description|process|subprocess|activity|type|purpose|justification)/i.test(c));
    const dataLines = hasHeader ? lines.slice(1) : lines;
    const list: Item[] = dataLines.map(l => {
      const parts = l.split(';').map(p => p.trim());
      // expected order: code;description;process;subprocess;activity;type;purpose;justification
      const code = String(parts[0] || '').trim();
      if (!code) return null;
      return {
        code,
        description: String(parts[1] || '').trim(),
        process: String(parts[2] || '').trim(),
        subprocess: String(parts[3] || '').trim(),
        activity: String(parts[4] || '').trim(),
        type: String(parts[5] || '').trim(),
        purpose: String(parts[6] || '').trim(),
        justification: String(parts[7] || '').trim()
      } as Item;
    }).filter(Boolean) as Item[];
    return list;
  };

  const parseExcelFile = async (f: File) => {
    try {
      const XLSX = await import('xlsx');
      const arrayBuffer = await f.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as any[][];
      // support header row or plain rows. Expected columns (either header or order):
      // code, description, process, subprocess, activity, type, purpose, justification
      const hasHeader = raw.length > 0 && raw[0].some((c: any) => typeof c === 'string' && /(code|description|process|subprocess|activity|type|purpose|justification)/i.test(String(c)));
      const rows = hasHeader ? raw.slice(1) : raw;
      const list: Item[] = rows.map(r => {
        if (!r || r.length === 0) return null;
        // if header present, map by index positions; otherwise assume standard order
        const code = String(r[0] || '').trim();
        if (!code) return null;
        return {
          code,
          description: String(r[1] || '').trim(),
          process: String(r[2] || '').trim(),
          subprocess: String(r[3] || '').trim(),
          activity: String(r[4] || '').trim(),
          type: String(r[5] || '').trim(),
          purpose: String(r[6] || '').trim(),
          justification: String(r[7] || '').trim()
        } as Item;
      }).filter(Boolean) as Item[];
      return list;
    } catch (err) {
      console.error('Erro parse Excel', err);
      return [];
    }
  };

  const handleFile = async (f: File | null) => {
    if (!f) return;
    try {
      let parsed: Item[] = [];
      if (/\.(xlsx|xls)$/i.test(f.name)) {
        parsed = await parseExcelFile(f);
      } else {
        const text = await f.text();
        parsed = parseCsvText(text);
      }

      // sanitize entries: trim and remove obviously-binary codes
      parsed = parsed.map(it => ({ code: String(it.code || '').trim(), description: String(it.description || '').trim(), type: String(it.type || '').trim(), process: it.process, subprocess: it.subprocess, activity: it.activity, purpose: it.purpose, justification: it.justification })).filter(it => {
        if (!it.code) return false;
        // reject codes with non-printable characters (likely binary garbage)
        if (!/^[\x20-\x7E]+$/.test(it.code)) return false;
        // reasonable length cap
        if (it.code.length > 200) return false;
        return true;
      });

  if (parsed.length === 0) return showToast('Nenhuma transação válida encontrada no arquivo', 'info');

      showConfirm(`Serão importados ${parsed.length} itens. Deseja continuar?`, async () => {
        hideConfirm();
        // chunked upload with progress and 413 handling
        try {
          const base = process.env.REACT_APP_API_URL || '';
          const url = base ? `${base.replace(/\/$/, '')}/api/sap-transactions/bulk` : '/api/sap-transactions/bulk';
          // initial chunk size (rows per request). We'll adapt on 413.
          let chunkSize = 500;
          const totalItems = parsed.length;
          const chunks = (arr: Item[], size: number) => {
            const out: Item[][] = [];
            for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
            return out;
          };

          // start progress UI
          startProgress(`Iniciando upload de ${totalItems} itens em lotes (tamanho inicial = ${chunkSize})`);
          uploadAbortRef.current = new AbortController();
          const signal = uploadAbortRef.current.signal;
          setProcessedCount(0);
          setTotalCount(totalItems);

          let insertedTotal = 0;
          let duplicatesTotal = 0;
          let sentTotal = 0;

          // We'll iterate sending chunks; if we hit 413, we'll retry the current remaining items with smaller chunk size
          let remaining = parsed.slice();
          let batchIndex = 0;
          while (remaining.length > 0) {
            if (signal.aborted) {
              addProgress(`Upload cancelado pelo usuário (${sentTotal}/${totalItems} enviados)`);
              break;
            }
            const currentChunks = chunks(remaining, chunkSize);
            let handled = false;
            for (let ci = 0; ci < currentChunks.length; ci++) {
              if (signal.aborted) break;
              const batch = currentChunks[ci];
              batchIndex += 1;
              addProgress(`Enviando lote ${batchIndex} (${batch.length} itens) ...`);
              try {
                const res = await axios.post(url, { items: batch }, { withCredentials: true, signal });
                const data = res && res.data ? res.data : {};
                insertedTotal += data.inserted || 0;
                duplicatesTotal += data.duplicates || 0;
                sentTotal += batch.length;
                setProcessedCount(sentTotal);
                addProgress(`Lote ${batchIndex} concluído (${sentTotal}/${totalItems}) - inseridos (acumulado) ${insertedTotal}`);
                // remove the items we just sent from remaining
                remaining = remaining.slice(batch.length);
                handled = true;
                // small delay to avoid overwhelming server (optional)
                await new Promise(r => setTimeout(r, 100));
              } catch (err: any) {
                // if server says payload too large, reduce chunk size and retry
                const status = err && err.response && err.response.status;
                console.warn('Erro upload lote', status || err.message || err);
                if (status === 413) {
                  // decrease chunk size and retry remaining
                  const newChunk = Math.max(50, Math.floor(chunkSize / 2));
                  addProgress(`413 recebido - reduzindo tamanho do lote de ${chunkSize} para ${newChunk} e tentando novamente`);
                  chunkSize = newChunk;
                  handled = false;
                  break; // break inner loop and re-chunk remaining with smaller size
                } else if (err.name === 'CanceledError' || err.message === 'canceled') {
                  addProgress('Upload cancelado');
                  remaining = [];
                  handled = true;
                  break;
                } else {
                  // for other errors, log and skip this batch to avoid blocking the whole import
                  addProgress(`Erro no lote ${batchIndex}: ${err.message || String(err)} - pulando lote`);
                  // remove batch so we don't retry it forever
                  remaining = remaining.slice(batch.length);
                  handled = true;
                }
              }
            }
            if (!handled && chunkSize <= 50) {
              // if even the small chunk fails, fallback to sending items one-by-one
              addProgress('Falha persistente em lotes; enviando itens um a um (mais lento)');
              for (let i = 0; i < remaining.length; i++) {
                if (signal.aborted) break;
                const single = [remaining[i]];
                try {
                  const res = await axios.post(url, { items: single }, { withCredentials: true, signal });
                  const data = res && res.data ? res.data : {};
                  insertedTotal += data.inserted || 0;
                  duplicatesTotal += data.duplicates || 0;
                  sentTotal += 1;
                  setProcessedCount(sentTotal);
                  addProgress(`Item ${sentTotal}/${totalItems} enviado`);
                } catch (err: any) {
                  addProgress(`Erro no item ${i + 1}: ${err.message || String(err)} - pulando`);
                }
              }
              remaining = [];
            }
          }

          endProgress(`Upload finalizado: enviados ${sentTotal}/${totalItems}, inseridos ${insertedTotal}, duplicados ${duplicatesTotal}`);
          showToast(`Upload finalizado: enviados ${sentTotal}/${totalItems}, inseridos ${insertedTotal}, duplicados ${duplicatesTotal}`, 'success', 6000);
          loadPage(1);
        } catch (err) {
          console.error('Erro ao importar em lotes', err);
          showToast('Erro no upload: veja console', 'error');
          endProgress('Erro no upload');
        } finally {
          if (uploadAbortRef.current) uploadAbortRef.current = null;
        }
      });
      
    } catch (err) {
      console.error('Erro ao processar arquivo', err);
      showToast('Erro ao processar arquivo: veja console', 'error');
    }
  };

  const openEdit = (it: Item) => setEditing(it);

  const saveEdit = async (next: Item) => {
    try {
      const base = process.env.REACT_APP_API_URL || '';
      const url = base ? `${base.replace(/\/$/, '')}/api/sap-transactions/${next._id}` : `/api/sap-transactions/${next._id}`;
  await axios.patch(url, { description: next.description, process: next.process, subprocess: next.subprocess, activity: next.activity, purpose: next.purpose, justification: next.justification, type: next.type }, { withCredentials: true });
      setEditing(null);
      // Update locally instead of reloading
      setAllItems(prev => prev.map(item => item._id === next._id ? { ...item, ...next } : item));
    } catch (err) { console.error('Erro salvar item', err); alert('Erro ao salvar'); }
  };

  const toggleSelect = (id?: string) => {
    if (!id) return;
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
  };

  const toggleSelectAllVisible = () => {
    // Toggle selection for all currently visible items
    const visibleIds = displayedItems.map(i => i._id).filter(Boolean) as string[];
    const s = new Set(selectedIds);
    const allSelected = visibleIds.every(id => s.has(id));
    if (allSelected) {
      visibleIds.forEach(id => s.delete(id));
    } else {
      visibleIds.forEach(id => id && s.add(id));
    }
    setSelectedIds(s);
  };

  const classifySelected = async () => {
  // classify selected items using batching across pages
  if (selectedIds.size === 0) return showToast('Selecione ao menos um item', 'info');
  // try to build list from current items; if some selected ids are not on this page, request them from backend
  const selectedArray = Array.from(selectedIds).filter(Boolean) as string[];
  const present = items.filter(i => i._id && selectedIds.has(i._id));
  const missingIds = selectedArray.filter(id => !present.some(p => p._id === id));
  let fetchedMissing: Item[] = [];
  if (missingIds.length > 0) {
    try {
      const base = process.env.REACT_APP_API_URL || '';
      const url = base ? `${base.replace(/\/$/, '')}/api/sap-transactions/by-ids` : '/api/sap-transactions/by-ids';
      let res;
      if (missingIds.length > 200) {
        res = await axios.post(url, { ids: missingIds }, { withCredentials: true });
      } else {
        res = await axios.get(url, { params: { ids: missingIds.join(',') }, withCredentials: true });
      }
      fetchedMissing = res && res.data && Array.isArray(res.data.items) ? res.data.items : [];
    } catch (err) {
      console.warn('Não foi possível buscar itens selecionados ausentes, prosseguindo com os visíveis', err);
    }
  }
  const toClassify = [...present, ...fetchedMissing];
  if (toClassify.length === 0) return showToast('Nenhum item selecionado disponível para classificação', 'info');
  await classifyItemsWithBatching(toClassify);
  };

  // helper: split array into chunks
  const chunkArray = <T,>(arr: T[], size: number) => {
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  };

  // classify a list of items in batches and apply results to backend
  const classifyItemsWithBatching = async (itemsToClassify: Item[], options?: { showProgress?: boolean }) => {
    if (!itemsToClassify || itemsToClassify.length === 0) return showToast('Nenhum item para classificar', 'info');
  const total = itemsToClassify.length;
  showToast(`Iniciando classificação de ${total} itens (tamanho inicial de lote = ${batchSize})`, 'info', 4000);
  startProgress(`Iniciando classificação de ${total} itens`);
    try {
      // setup abort controller for cancellation
      classifyAbortRef.current = new AbortController();
      const signal = classifyAbortRef.current.signal;
      setProcessedCount(0);
      setTotalCount(total);
      const base = process.env.REACT_APP_API_URL || '';
      const classifyUrl = base ? `${base.replace(/\/$/, '')}/api/ai/classify` : '/api/ai/classify';
  // prepare conversation when contextMode is enabled
      let conversationMessages: any[] | null = null;
      if (contextMode) {
        conversationMessages = [{ role: 'system', content: 'You are an assistant that classifies SAP transaction codes (TCODE) and Fiori apps (FIORI) into a taxonomy: Processo > Subprocesso > Atividade. For each item also provide: purpose and justification. Return JSON array.' }];
      }
      // helper: try to parse assistantText/raw JSON even when wrapped in code fences or truncated
      const tryParseAssistantJSON = (text: string) => {
        if (!text || typeof text !== 'string') return null;
        // attempt quick JSON parse
        try { return JSON.parse(text); } catch (e) {}
        // strip markdown fences
        const cleaned = text.replace(/```(?:json)?/gi, '').replace(/```/g, '').trim();
        try { return JSON.parse(cleaned); } catch (e) {}
        // find first bracket and try to progressively parse until valid
        const first = Math.min(...['[','{'].map(c => { const i = cleaned.indexOf(c); return i === -1 ? Number.MAX_SAFE_INTEGER : i; }));
        if (first === Number.MAX_SAFE_INTEGER) return null;
        const candidate = cleaned.slice(first);
        for (let end = candidate.length; end > 0; end--) {
          try { const sub = candidate.slice(0, end); const p = JSON.parse(sub); return p; } catch (e) { /* continue trimming */ }
        }
        return null;
      };

      // recursive batch processor: attempts to classify a batch; on unparseable response it splits the batch and retries
      const processBatch = async (batchItems: Item[], label?: string): Promise<any[]> => {
        if (!batchItems || batchItems.length === 0) return [];
        if (signal.aborted) { addProgress('Classificação abortada'); return []; }
        const payloadItems = batchItems.map(i => ({ code: i.code, description: i.description, type: i.type }));
        const batchLabel = label || `${batchItems.length} itens`;
        addProgress(`Enviando lote (${batchLabel})`);
        let bodyToSend: any = { items: payloadItems, prompt, model, temperature, topP, maxTokens, presencePenalty, frequencyPenalty };
        if (useWebSearch) bodyToSend.tools = [{ type: 'web_search' }];
        if (contextMode && conversationMessages) {
          const itemsText = payloadItems.map((it: any) => `${it.code}${it.type ? ` [${it.type}]` : ''}${it.description ? ` | ${it.description}` : ''}`).join('\n');
          const msgs = [{ role: 'system', content: prompt }, { role: 'user', content: `Items:\n${itemsText}` }];
          bodyToSend.messages = msgs.concat(conversationMessages || []);
        }
        // log sent
        addLogEntry('sent', `Classify -> POST ${classifyUrl} (${batchItems.length})`, bodyToSend);
        try {
          const res = await axios.post(classifyUrl, bodyToSend, { withCredentials: true, signal });
          addProgress(`Resposta recebida (lote ${batchItems.length})`);
          const data = res && res.data ? res.data : null;
          addLogEntry('received', `Classify <- response ${classifyUrl} (${batchItems.length})`, data, data?.usage);
          // if backend returned parsed items, use them
          if (data && Array.isArray(data.items) && data.items.length > 0) {
            // append assistantText to conversation if needed
            if (contextMode && conversationMessages && data.assistantText) conversationMessages.push({ role: 'assistant', content: data.assistantText });
            return data.items;
          }
          // try to recover from assistantText/raw
          const textCandidate = data && (data.assistantText || data.raw || (typeof data.raw === 'string' ? data.raw : null));
          const parsed = tryParseAssistantJSON(typeof textCandidate === 'string' ? textCandidate : (data && data.raw && typeof data.raw === 'string' ? data.raw : ''));
          if (parsed && (Array.isArray(parsed) || parsed.items)) {
            const itemsArray = Array.isArray(parsed) ? parsed : parsed.items;
            return itemsArray;
          }
          // If response couldn't be parsed and batch larger than 1, split and retry
          if (batchItems.length > 1) {
            addProgress(`Resposta não parseável para lote de ${batchItems.length}; dividindo e tentando novamente`);
            const half = Math.ceil(batchItems.length / 2);
            const left = batchItems.slice(0, half);
            const right = batchItems.slice(half);
            const leftRes = await processBatch(left, `${left.length}`);
            const rightRes = await processBatch(right, `${right.length}`);
            return [...(leftRes || []), ...(rightRes || [])];
          }
          // if single item and still unparseable, log and skip
          addProgress(`Item único não parseável: pulando ${batchItems[0].code}`);
          return [];
        } catch (err: any) {
          const status = err?.response?.status;
          const errData = err?.response?.data;
          addLogEntry('received', `Classify <- error ${classifyUrl} (${batchItems.length})`, { message: err?.message || String(err), status, data: errData }, errData?.usage || errData?.raw?.usage);
          addProgress(`Erro na requisição para lote ${batchItems.length}: ${err?.message || String(err)}`);
          // on certain errors (413, 429, 502) try splitting
          if ((status === 413 || status === 429 || status === 502) && batchItems.length > 1) {
            addProgress(`Erro ${status} recebido; dividindo lote de ${batchItems.length} e re-tentando`);
            const half = Math.ceil(batchItems.length / 2);
            const left = batchItems.slice(0, half);
            const right = batchItems.slice(half);
            const leftRes = await processBatch(left, `${left.length}`);
            const rightRes = await processBatch(right, `${right.length}`);
            return [...(leftRes || []), ...(rightRes || [])];
          }
          // otherwise skip these items
          return [];
        }
      };

      // process initial batches using configured batchSize but allow recursive splitting on failures
      const initialChunks = chunkArray(itemsToClassify, Math.max(1, batchSize || 10));
      let processedLocal = 0;
      for (let i = 0; i < initialChunks.length; i++) {
        if (signal.aborted) break;
        const chunk = initialChunks[i];
        const results = await processBatch(chunk);
        console.log(`📦 Chunk ${i+1}/${initialChunks.length}: recebeu ${results?.length || 0} resultados da IA`, results);
        console.log(`📋 itemsToClassify tem ${itemsToClassify.length} itens:`, itemsToClassify.map(it => ({ code: it.code, _id: it._id })));
        // apply updates sequentially to backend and collect successful updates
        const successfulUpdates: Array<{ _id: string; data: any }> = [];
        for (const ai of results || []) {
          const aiCodeRaw = ai.code || ai.Code || ai.CODE || ai.tcode || ai.TCODE || '';
          console.log(`🔍 Procurando item para código da IA:`, { 
            aiObj: ai, 
            code: aiCodeRaw, 
            codeType: typeof aiCodeRaw, 
            startsWithUnderscore: String(aiCodeRaw).startsWith('_'),
            trimmed: String(aiCodeRaw).trim()
          });
          // Try multiple matching strategies - case insensitive and trim
          const codeToMatch = String(aiCodeRaw).trim();
          const codeToMatchUpper = codeToMatch.toUpperCase();
          const codeWithUnderscore = '_' + codeToMatch; // Try adding underscore if missing
          const codeWithUnderscoreUpper = codeWithUnderscore.toUpperCase();
          
          const found = itemsToClassify.find(it => {
            const itCode = String(it.code || '').trim();
            const itCodeUpper = itCode.toUpperCase();
            // Try exact match, case-insensitive, with/without underscore, then by _id
            return itCode === codeToMatch || 
                   itCodeUpper === codeToMatchUpper || 
                   itCode === codeWithUnderscore ||
                   itCodeUpper === codeWithUnderscoreUpper ||
                   it._id === ai._id ||
                   it._id === ai.id;
          });
          console.log(`${found ? '✅' : '❌'} Item ${found ? 'encontrado' : 'NÃO encontrado'}:`, found ? { code: found.code, _id: found._id } : 'null');
          if (found && found._id) {
            try {
              const updateData = { process: ai.process || '', subprocess: ai.subprocess || '', activity: ai.activity || '', purpose: ai.purpose || '', justification: ai.justification || '', type: ai.type || found.type || '' };
              await axios.patch((process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}` : '') + `/api/sap-transactions/${found._id}`, updateData, { withCredentials: true });
              console.log(`✅ Classificação salva para ${found.code}:`, updateData);
              successfulUpdates.push({ _id: found._id, data: updateData });
              addProgress(`Registro salvo: ${found.code}`);
            } catch (err) {
              console.warn('Erro ao aplicar patch para', found._id, err);
            }
          }
        }
        // Update all successful items locally in a single batch
        if (successfulUpdates.length > 0) {
          setAllItems(prev => prev.map(item => {
            const update = successfulUpdates.find(u => u._id === item._id);
            return update ? { ...item, ...update.data } : item;
          }));
        }
  processedLocal += chunk.length;
  setProcessedCount(prev => Math.min(total, (prev || 0) + chunk.length));
  addProgress(`Progresso: ${processedLocal}/${total}`);
  showToast(`Processados ${processedLocal}/${total}`, 'info', 1500);
      }
  showToast('Classificação completa!', 'success', 3000);
  setSelectedIds(new Set());
  endProgress('Classificação concluída');
  } catch (err) {
      console.error('Erro na classificação em lotes', err);
      showToast('Erro na classificação IA', 'error');
      endProgress('Erro na classificação');
    }
  };

  // classify all items in the repository (fetch all pages then classify)
  const classifyAll = async () => {
    // ask confirmation including total count
    const totalCount = total || 0;
    if (!totalCount) return showToast('Nenhum item disponível para classificar', 'info');
    showConfirm(`Todos os itens serão reclassificados (total de itens = ${totalCount}). Tem certeza que deseja prosseguir?`, async () => {
      hideConfirm();
      // fetch all items page by page
      try {
        const base = process.env.REACT_APP_API_URL || '';
        const urlBase = base ? `${base.replace(/\/$/, '')}/api/sap-transactions` : '/api/sap-transactions';
        const pageSizeFetch = 500; // reasonable page size
        let pageNum = 1;
        const allItems: Item[] = [];
        while (true) {
          const res = await axios.get(`${urlBase}?page=${pageNum}&pageSize=${pageSizeFetch}`, { withCredentials: true });
          const fetched: Item[] = res.data.items || [];
          allItems.push(...fetched);
          if (!fetched || fetched.length < pageSizeFetch) break;
          pageNum += 1;
        }
        if (allItems.length === 0) return showToast('Nenhum item recuperado para classificação', 'info');
        await classifyItemsWithBatching(allItems);
      } catch (err) {
        console.error('Erro ao buscar todos itens', err);
        showToast('Erro ao recuperar itens para classificar', 'error');
      }
    });
  };

  const deleteSelected = async () => {
  if (selectedIds.size === 0) return showToast('Selecione ao menos um item para excluir', 'info');
    const ids = Array.from(selectedIds).filter(Boolean) as string[];
    if (!ids.length) return alert('Nenhum id válido selecionado');
    showConfirm(`Confirma exclusão de ${ids.length} itens selecionados?`, async () => {
      hideConfirm();
      try {
        const base = process.env.REACT_APP_API_URL || '';
        const url = base ? `${base.replace(/\/$/, '')}/api/sap-transactions/bulk-delete` : '/api/sap-transactions/bulk-delete';
        const res = await axios.delete(url, { data: { ids }, withCredentials: true });
        if (res && res.data && typeof res.data.deletedCount !== 'undefined') {
          showToast(`Excluídos ${res.data.deletedCount} itens.`, 'success');
        } else {
          showToast('Exclusão concluída. Atualizando...', 'success');
        }
        setSelectedIds(new Set());
        loadPage(1);
      } catch (err) {
        console.error('Erro ao excluir itens', err);
        showToast('Erro ao excluir itens: veja console', 'error');
      }
    });
  };

  // Handlers for DataTable
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(1);
  };

  const handleColumnFilter = (column: string, value: string) => {
    setColumnFilters(prev => ({ ...prev, [column]: value }));
    setPage(1); // voltar para primeira página ao filtrar
  };

  // Ordenar dados no frontend
  const sortedItems = [...allItems].sort((a, b) => {
    const aVal = (a as any)[sortBy];
    const bVal = (b as any)[sortBy];
    if (aVal === bVal) return 0;
    const comparison = aVal < bVal ? -1 : 1;
    return sortDir === 'asc' ? comparison : -comparison;
  });

  // Filtrar dados ordenados
  const filteredItems = sortedItems.filter(item => {
    return Object.entries(columnFilters).every(([column, filterValue]) => {
      if (!filterValue) return true;
      const itemValue = String((item as any)[column] || '').toLowerCase();
      return itemValue.includes(filterValue.toLowerCase());
    });
  });

  // Atualizar total com base nos itens filtrados
  const totalFiltered = filteredItems.length;

  // Paginar dados filtrados
  const paginatedItems = filteredItems.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const displayedItems = paginatedItems;

  // Columns definition
  const columns: DataTableColumn<Item>[] = [
    {
      key: '_select',
      label: 'Seleção',
      minWidth: 50,
      sortable: false,
      checkboxColumn: true,
      allChecked: displayedItems.length > 0 && displayedItems.every(item => item._id && selectedIds.has(item._id)),
      onCheckboxHeaderClick: toggleSelectAllVisible,
      render: (value: any, item: Item) => (
        <input 
          type="checkbox" 
          checked={item._id ? selectedIds.has(item._id) : false}
          onChange={() => toggleSelect(item._id)}
          style={{ cursor: 'pointer' }}
        />
      )
    },
    {
      key: 'code',
      label: 'Código',
      minWidth: 120,
      render: (value: any, item: Item) => (
        <span style={{ fontWeight: 500 }}>{item.code}</span>
      )
    },
    {
      key: 'description',
      label: 'Descrição',
      minWidth: 200,
      render: (value: any, item: Item) => <span>{item.description || ''}</span>
    },
    {
      key: 'process',
      label: 'Processo',
      minWidth: 150,
      render: (value: any, item: Item) => <span>{item.process || ''}</span>
    },
    {
      key: 'subprocess',
      label: 'Subprocesso',
      minWidth: 150,
      render: (value: any, item: Item) => <span>{item.subprocess || ''}</span>
    },
    {
      key: 'activity',
      label: 'Atividade',
      minWidth: 150,
      render: (value: any, item: Item) => <span>{item.activity || ''}</span>
    },
    {
      key: 'type',
      label: 'Tipo',
      minWidth: 100,
      render: (value: any, item: Item) => <span>{item.type || ''}</span>
    },
    {
      key: 'purpose',
      label: 'Finalidade',
      minWidth: 150,
      render: (value: any, item: Item) => <span>{item.purpose || ''}</span>
    },
    {
      key: 'justification',
      label: 'Justificativa',
      minWidth: 180,
      render: (value: any, item: Item) => <span>{item.justification || ''}</span>
    },
    {
      key: 'actions',
      label: 'Ações',
      minWidth: 100,
      sortable: false,
      render: (value: any, item: Item) => (
        <ActionMenu
          actions={[
            {
              key: 'edit',
              label: 'Editar',
              icon: Icons.Pencil,
              onClick: () => openEdit(item)
            }
          ]}
        />
      )
    }
  ];

  // items to show in the indicators drawer table (prefer activityItems if populated, otherwise derive from selectedFilter)
  const drawerTableItems: Item[] = (activityItems && activityItems.length) ? activityItems : (selectedFilter ? getDataSource().filter(it => {
    if (selectedFilter?.process && ((it.process || '').trim() !== selectedFilter.process)) return false;
    if (selectedFilter?.subprocess && ((it.subprocess || '').trim() !== selectedFilter.subprocess)) return false;
    if (selectedFilter?.activity && ((it.activity || '').trim() !== selectedFilter.activity)) return false;
    return true;
  }) : []);

  return (
    <>
      

  <div className="container py-5" style={{ marginTop: 80, paddingBottom: 120 }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div style={{ minWidth: 420 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div>
                <h1 style={{ marginBottom: 6 }}>Benchmark de Perfis SAP</h1>
                <p className="lead" style={{ marginBottom: 6 }}>Repositório central de transações / APPs classificados.</p>
              </div>
              <div className="header-badge">
                <span className="badge-item">Modelo: {model}</span>
                <span className="badge-item">Batch: {batchSize}</span>
              </div>
            </div>
          </div>
            <div className="d-flex gap-2 align-items-center benchmark-controls">
            <label className="btn btn-outline-primary mb-0">
              Carregar CSV/CSV;/.XLSX
              <input type="file" accept=".csv,.txt,.xlsx,.xls" style={{ display: 'none' }} onChange={e => handleFile(e.target.files ? e.target.files[0] : null)} />
            </label>
            <button className="btn btn-outline-primary mb-0" onClick={() => setShowAddModal(true)}>Adicionar Item</button>
            <button className="btn btn-outline-danger" onClick={deleteSelected} title="Excluir selecionados">Excluir selecionados</button>
            {/* Parâmetros consolidated control (Prompt button removed) */}
            <button className="btn btn-outline-primary btn-params" onClick={() => setShowPromptEditor(true)}>Parâmetros</button>
            <button className="btn btn-sm btn-outline-primary ms-2" onClick={async () => {
              try {
                const base = process.env.REACT_APP_API_URL || '';
                const url = base ? `${base.replace(/\/$/, '')}/api/sap-transactions/export?format=xlsx` : '/api/sap-transactions/export?format=xlsx';
                window.location.href = url;
              } catch (err) {
                console.error('Erro iniciar export', err);
                showToast('Erro ao iniciar exportação', 'error');
              }
            }}>Exportar Excel</button>
            <button className="btn btn-sm btn-outline-primary ms-2" onClick={() => setShowChatModal(true)}>Chat IA</button>

            {/* classify menu */}
            <div style={{ position: 'relative', display: 'inline-block', marginLeft: 8 }}>
              <button className="btn btn-primary" onClick={() => { setClassifyMenuOpen(s => !s); }}>{'Classificar ▾'}</button>
              {classifyMenuOpen && (
                <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 1500, marginTop: 6, minWidth: 220 }}>
                  <div className="card custom-menu-card" style={{ padding: 6 }} onMouseLeave={() => setClassifyMenuOpen(false)}>
                    <button className="btn btn-light w-100 mb-1 text-start" onClick={() => { setClassifyMenuOpen(false); classifySelected(); }}>Classificar selecionadas</button>
                    <button className="btn btn-light w-100 text-start" onClick={() => { setClassifyMenuOpen(false); classifyAll(); }}>Classificar todas</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {loading && loadingProgress.total > 0 && (
          <ProgressBar 
            current={loadingProgress.current} 
            total={loadingProgress.total}
            message="Carregando transações SAP..."
          />
        )}
        {loading && loadingProgress.total === 0 && (
          <div>Carregando...</div>
        )}
        {!loading && (
          <DataTable
            columns={columns}
            data={displayedItems}
            keyExtractor={(item: Item) => item._id || item.code}
            sortColumn={sortBy}
            sortDirection={sortDir}
            onSort={handleSort}
            onFilter={handleColumnFilter}
            filters={columnFilters}
            striped
            hover
            responsive
            emptyMessage="Nenhum item disponível"
            pagination={true}
            currentPage={page}
            pageSize={pageSize}
            totalItems={totalFiltered}
            onPageChange={(newPage) => {
              setPage(newPage);
            }}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
              localStorage.setItem('benchmark_pageSize', String(size));
            }}
            pageSizeOptions={[10, 25, 50, 100, 200, 500]}
          />
        )}
      </div>

        {/* Edit modal */}
        {editing && (
          <div className="users-overlay">
            <div className="users-overlay-box" onClick={e => e.stopPropagation()}>
              <button className="modal-close-btn" aria-label="Fechar" onClick={() => setEditing(null)}>×</button>
              <h4>Editar {editing.code}</h4>
              <div className="mb-2">
                <label className="form-label">Descrição</label>
                <input className="form-control" value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div className="mb-2">
                <label className="form-label">Processo</label>
                <input className="form-control" value={editing.process || ''} onChange={e => setEditing({ ...editing, process: e.target.value })} />
              </div>
              <div className="mb-2">
                <label className="form-label">Subprocesso</label>
                <input className="form-control" value={editing.subprocess || ''} onChange={e => setEditing({ ...editing, subprocess: e.target.value })} />
              </div>
              <div className="mb-3">
                <label className="form-label">Atividade</label>
                <input className="form-control" value={editing.activity || ''} onChange={e => setEditing({ ...editing, activity: e.target.value })} />
              </div>
              <div className="mb-2">
                <label className="form-label">Tipo</label>
                <select className="form-select" value={editing.type || ''} onChange={e => setEditing({ ...editing, type: e.target.value })}>
                  <option value="">(não informado)</option>
                  <option value="TCODE">TCODE</option>
                  <option value="FIORI">FIORI</option>
                </select>
              </div>
              <div className="mb-2">
                <label className="form-label">Finalidade (purpose)</label>
                <input className="form-control" value={editing.purpose || ''} onChange={e => setEditing({ ...editing, purpose: e.target.value })} />
              </div>
              <div className="mb-3">
                <label className="form-label">Justificativa</label>
                <input className="form-control" value={editing.justification || ''} onChange={e => setEditing({ ...editing, justification: e.target.value })} />
              </div>
              <div className="d-flex gap-2 justify-content-end">
                <button className="btn btn-secondary" onClick={() => setEditing(null)}>Cancelar</button>
                <button className="btn btn-primary" onClick={() => saveEdit(editing)}>Salvar</button>
              </div>
            </div>
            {/* Export colocado no topo dos controles; botão duplicado removido aqui */}

            
          </div>
        )}

        {/* Add Item modal (separate) */}
        {showAddModal && (
          <div className="users-overlay">
            <div className="users-overlay-box" onClick={e => e.stopPropagation()} style={{ width: 520 }}>
              <button className="modal-close-btn" aria-label="Fechar" onClick={() => setShowAddModal(false)}>×</button>
              <h4>Adicionar Item manualmente</h4>
              <div className="mb-2">
                <label className="form-label">Código</label>
                <input className="form-control" value={newItemCode} onChange={e => setNewItemCode(e.target.value)} />
              </div>
              <div className="mb-2">
                <label className="form-label">Descrição</label>
                <input className="form-control" value={newItemDescription} onChange={e => setNewItemDescription(e.target.value)} />
              </div>
              <div className="mb-2">
                <label className="form-label">Tipo</label>
                <select className="form-select" value={newItemType} onChange={e => setNewItemType(e.target.value)}>
                  <option value="TCODE">TCODE</option>
                  <option value="FIORI">FIORI</option>
                </select>
              </div>
              <div className="d-flex gap-2 justify-content-end">
                <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={async () => {
                  if (!newItemCode.trim()) return showToast('Código é obrigatório', 'info');
                  try {
                    const base = process.env.REACT_APP_API_URL || '';
                    const url = base ? `${base.replace(/\/$/, '')}/api/sap-transactions/bulk` : '/api/sap-transactions/bulk';
          const res = await axios.post(url, { items: [{ code: newItemCode.trim(), description: newItemDescription.trim(), source: 'manual', type: newItemType }] }, { withCredentials: true });
                    if (res && res.data) {
                      const r = res.data;
                      showToast(`Resultado: recebidos ${r.received || 1}, inseridos ${r.inserted || 0}, duplicados ${r.duplicates || 0}`, 'success');
                    }
                    setShowAddModal(false);
            setNewItemCode(''); setNewItemDescription(''); setNewItemType('TCODE');
                    loadPage(1);
                  } catch (err) { console.error('Erro ao adicionar item', err); showToast('Erro ao adicionar item', 'error'); }
                }}>Salvar</button>
              </div>
            </div>
          </div>
        )}

        {/* Toast component */}
        <div style={{ position: 'fixed', right: 20, bottom: 20, zIndex: 2000 }}>
          {toast.visible && (
            <div className={`alert ${toast.type === 'error' ? 'alert-danger' : toast.type === 'success' ? 'alert-success' : 'alert-info'} users-toast`} role="alert" style={{ minWidth: 280 }}>
              {toast.message}
            </div>
          )}
          {/* Progress panel */}
          {progressActive && (
            <div className="card" style={{ minWidth: 320, maxWidth: 420, background: 'rgba(255,255,255,0.96)', padding: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ fontWeight: 700 }}>Processando classificação</div>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => {
                  // cancel ongoing classify or upload
                  if (classifyAbortRef.current) {
                    classifyAbortRef.current.abort();
                  }
                  if (uploadAbortRef.current) {
                    uploadAbortRef.current.abort();
                  }
                  addProgress('Cancelamento solicitado...');
                }}>Cancelar</button>
              </div>

              <div style={{ height: 10, background: '#eee', borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
                <div style={{ width: `${totalCount ? Math.round((processedCount / totalCount) * 100) : 0}%`, height: '100%', background: 'linear-gradient(90deg,var(--phoenix-primary),#4a90e2)' }} />
              </div>
              <div style={{ fontSize: 13, marginBottom: 6 }}>{processedCount}/{totalCount} itens processados ({totalCount ? Math.round((processedCount / totalCount) * 100) : 0}%)</div>
              <div style={{ maxHeight: 220, overflow: 'auto' }}>
                {progressLines.map((ln, idx) => (
                  <div key={idx} style={{ fontSize: 13, marginBottom: 6 }}>{ln}</div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Confirm modal */}
        {confirm.visible && (
          <div className="users-overlay">
            <div className="users-overlay-box" onClick={e => e.stopPropagation()} style={{ width: 520 }}>
              <button className="modal-close-btn" aria-label="Fechar" onClick={() => hideConfirm()}>×</button>
              <h4>Confirmação</h4>
              <p>{confirm.message}</p>
              <div className="d-flex gap-2 justify-content-end">
                <button className="btn btn-secondary" onClick={() => hideConfirm()}>Cancelar</button>
                <button className="btn btn-danger" onClick={() => { if (confirm.onConfirm) confirm.onConfirm(); }}>Confirmar</button>
              </div>
            </div>
          </div>
        )}

        {/* Prompt editor modal */}
        {/* Chat IA modal */}
        {showChatModal && (
          <div className="users-overlay">
            <div className="users-overlay-box" style={{ width: 720, maxWidth: '95%' }} onClick={e => e.stopPropagation()}>
              <button className="modal-close-btn" aria-label="Fechar" onClick={() => setShowChatModal(false)}>×</button>
              <h4>Chat IA</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ maxHeight: 320, overflow: 'auto', padding: 8, border: '1px solid #eee', borderRadius: 6, background: '#fff' }}>
                  {chatMessages.length === 0 && (<div style={{ color: '#666' }}>Conversa vazia. Envie uma mensagem abaixo.</div>)}
                  {chatMessages.map((m, i) => (
                    <div key={i} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 12, color: '#333', fontWeight: 700 }}>{m.role}</div>
                      <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{m.content}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="form-control" value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(); } }} placeholder="Escreva uma mensagem para a IA..." />
                  <button className="btn btn-primary" onClick={sendChatMessage} disabled={chatLoading}>{chatLoading ? 'Enviando...' : 'Enviar'}</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showPromptEditor && (
          <div className="users-overlay">
            <div className="users-overlay-box" style={{ width: '760px', maxWidth: '95%' }} onClick={e => e.stopPropagation()}>
              <button className="modal-close-btn" aria-label="Fechar" onClick={() => setShowPromptEditor(false)}>×</button>
              <h4>Editar Prompt da IA</h4>
              <div className="mb-2">
                <label className="form-label">Modelo</label>
                <select className="form-select" value={model} onChange={e => setModel(e.target.value)}>
                  {availableModels && availableModels.length > 0 ? (
                    availableModels.map(m => (<option key={m} value={m}>{m}</option>))
                  ) : (
                    <>
                      <option value="gpt-3.5-turbo">gpt-3.5-turbo</option>
                      <option value="gpt-3.5-turbo-16k">gpt-3.5-turbo-16k</option>
                      <option value="gpt-4o">gpt-4o</option>
                      <option value="gpt-4">gpt-4</option>
                      <option value="gpt-5">gpt-5</option>
                    </>
                  )}
                </select>
              </div>
              <div className="mb-2">
                <label className="form-label">Tamanho do lote</label>
                <input type="number" min={1} className="form-control" value={batchSize} onChange={e => setBatchSize(Number(e.target.value) || 1)} />
              </div>
              <div className="mb-2">
                <label className="form-label">Temperature (0-2)</label>
                <input type="number" step="0.01" min={0} max={2} className="form-control" value={temperature} onChange={e => setTemperature(Number(e.target.value))} />
              </div>
              <div className="mb-2">
                <label className="form-label">Top P (0-1)</label>
                <input type="number" step="0.01" min={0} max={1} className="form-control" value={topP} onChange={e => setTopP(Number(e.target.value))} />
              </div>
              <div className="mb-2">
                <label className="form-label">Max tokens</label>
                <input type="number" min={64} className="form-control" value={maxTokens} onChange={e => setMaxTokens(Number(e.target.value) || 1500)} />
              </div>
              <div className="mb-2">
                <label className="form-label">Presence penalty</label>
                <input type="number" step="0.01" min={-2} max={2} className="form-control" value={presencePenalty} onChange={e => setPresencePenalty(Number(e.target.value))} />
              </div>
              <div className="mb-2">
                <label className="form-label">Frequency penalty</label>
                <input type="number" step="0.01" min={-2} max={2} className="form-control" value={frequencyPenalty} onChange={e => setFrequencyPenalty(Number(e.target.value))} />
              </div>
              <div className="mb-2 form-check">
                <input id="contextModeCheck" className="form-check-input" type="checkbox" checked={contextMode} onChange={e => setContextMode(e.target.checked)} />
                <label className="form-check-label" htmlFor="contextModeCheck">Análise em contexto (manter conversa entre lotes)</label>
              </div>
              <div className="mb-2 form-check">
                <input id="useWebSearchCheck" className="form-check-input" type="checkbox" checked={useWebSearch} onChange={e => setUseWebSearch(e.target.checked)} />
                <label className="form-check-label" htmlFor="useWebSearchCheck">Habilitar Web Search (OpenAI tools)</label>
              </div>
              <div className="mb-2">
                <label className="form-label">Prompt (objetivo)</label>
                <textarea className="form-control" rows={6} value={prompt} onChange={e => setPrompt(e.target.value)} />
              </div>
              <div className="d-flex gap-2 justify-content-end">
                <button className="btn btn-secondary" onClick={() => setShowPromptEditor(false)}>Fechar</button>
                <button className="btn btn-primary" onClick={() => {
                  localStorage.setItem('benchmark_prompt', prompt);
                  localStorage.setItem('benchmark_model', model);
                  localStorage.setItem('benchmark_batchSize', String(batchSize));
                  localStorage.setItem('benchmark_contextMode', String(contextMode));
                  localStorage.setItem('benchmark_temperature', String(temperature));
                  localStorage.setItem('benchmark_topP', String(topP));
                  localStorage.setItem('benchmark_maxTokens', String(maxTokens));
                  localStorage.setItem('benchmark_presencePenalty', String(presencePenalty));
                  localStorage.setItem('benchmark_frequencyPenalty', String(frequencyPenalty));
                  localStorage.setItem('benchmark_useWebSearch', String(useWebSearch));
                  setShowPromptEditor(false);
                }}>Salvar</button>
              </div>
            </div>
          </div>
        )}
  {/* Floating Log button (ensure it's above the pagination bar) */}
  <button title="Log IA" onClick={() => setShowLogModal(true)} style={{ position: 'fixed', left: 20, bottom: 20, zIndex: 2200, borderRadius: 999, padding: '10px 14px', background: theme === 'dark' ? '#111' : '#222', color: '#fff', border: 'none', boxShadow: '0 6px 14px rgba(0,0,0,0.18)' }}>Log</button>

        {/* Floating Indicators button (vertical, right centered) */}
  <button title="Indicadores" onClick={async () => { setShowIndicators(true); setDrillState({ level: 'process' }); await fetchAllItemsForIndicators(); }} className="indicators-float-btn">Indicadores</button>

  {/* overlay when open */}
  {showIndicators && (<div onClick={() => setShowIndicators(false)} style={{ position: 'fixed', left: 0, top: 0, width: '100%', height: '100%', zIndex: 2050, background: 'rgba(0,0,0,0.18)' }} />)}

  {/* Indicators sliding drawer */}
  <div className="indicators-drawer" style={{ right: showIndicators ? 0 : '-50%' }}>
          <div style={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h4 style={{ margin: 0 }}>Indicadores</h4>
              <div>
                {drillState.level !== 'process' && (<button className="btn btn-sm btn-outline-secondary me-2" onClick={goBackDrill}>Voltar</button>)}
                <button className="btn btn-sm btn-secondary" onClick={() => setShowIndicators(false)}>Fechar</button>
              </div>
            </div>
            <div style={{ marginBottom: 8, color: '#666' }}>Clique em uma barra para fazer drill-down; use Voltar para subir um nível.</div>
            {/* Chart area (top half) */}
            <div className="indicators-chart-area">
              {drillState.level === 'process' && (
                <div>
                  {indicatorsLoading ? <div>Carregando indicadores...</div> : (
                    <BarList data={
                      indicatorsTree && indicatorsTree.length ?
                        // ensure sorted desc by count
                        indicatorsTree.slice().sort((a: any, b: any) => (b.count || 0) - (a.count || 0)).map((p: any) => ({ label: p.process, count: p.count }))
                        : getProcessStats()
                    } onBarClick={handleBarClick} />
                  )}
                </div>
              )}
              {drillState.level === 'subprocess' && (
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Processo: {drillState.process}</div>
                  <BarList data={
                    indicatorsTree && indicatorsTree.length ?
                      // find process then sort its subprocesses by count desc
                      (indicatorsTree.find((p: any) => p.process === drillState.process)?.subprocesses || []).slice().sort((a: any, b: any) => (b.count || 0) - (a.count || 0)).map((sp: any) => ({ label: sp.subprocess, count: sp.count }))
                      : getSubprocessStats(drillState.process)
                  } onBarClick={handleBarClick} />
                </div>
              )}
              {drillState.level === 'activity' && (
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Processo: {drillState.process} › Subprocesso: {drillState.subprocess}</div>
                  <BarList data={
                    indicatorsTree && indicatorsTree.length ?
                      (indicatorsTree.find((p: any) => p.process === drillState.process)?.subprocesses.find((s: any) => s.subprocess === drillState.subprocess)?.activities || []).slice().sort((a: any, b: any) => (b.count || 0) - (a.count || 0)).map((a: any) => ({ label: a.activity, count: a.count }))
                      : getActivityStats(drillState.process, drillState.subprocess)
                  } onBarClick={handleBarClick} />
                </div>
              )}
            </div>

            {/* Table area (bottom half) - visible only on activity level */}
            {drillState.level === 'activity' && (
              <div className="indicators-table-area">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h5 style={{ margin: 0 }}>Itens</h5>
                <div style={{ fontSize: 12, color: '#666' }}>{selectedFilter ? `${selectedFilter.process || ''}${selectedFilter.subprocess ? ' › ' + selectedFilter.subprocess : ''}${selectedFilter.activity ? ' › ' + selectedFilter.activity : ''}` : 'Selecione uma seção no gráfico'}</div>
              </div>
              <div style={{ height: 'calc(100% - 36px)', overflow: 'auto', position: 'relative' }}>
                {activityLoading ? (
                  <div className="indicators-table-spinner" style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="spinner-border" role="status" aria-hidden="true" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                  </div>
                ) : (
                  <table className="table table-sm">
                    <thead>
                      <tr><th>Processo</th><th>Subprocesso</th><th>Atividade</th><th>Code</th><th>Tipo</th></tr>
                    </thead>
                    <tbody>
                      {drawerTableItems.map(it => (
                        <tr key={it._id || it.code}><td>{it.process}</td><td>{it.subprocess}</td><td>{it.activity}</td><td>{it.code}</td><td>{it.type}</td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            )}
          </div>
        </div>

        {/* Log modal */}
        {showLogModal && (
          <div className="users-overlay">
            <div className="users-overlay-box" style={{ width: '760px', maxWidth: '95%' }} onClick={e => e.stopPropagation()}>
              <button className="modal-close-btn" aria-label="Fechar" onClick={() => setShowLogModal(false)}>×</button>
              <h4>Log de Mensagens IA (RAW)</h4>
              <div style={{ maxHeight: 520, overflow: 'auto', border: '1px solid #eee', padding: 8, borderRadius: 6, background: '#fff' }}>
                {logEntries.length === 0 && (<div style={{ color: '#666' }}>Nenhum log registrado ainda.</div>)}
                {logEntries.map(entry => (
                  <div key={entry.id} style={{ borderBottom: '1px solid #f0f0f0', padding: '8px 4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{entry.direction === 'sent' ? 'ENVIADO' : 'RECEBIDO'} — {entry.title}</div>
                      <div style={{ fontSize: 12, color: '#666' }}>{entry.ts}</div>
                    </div>
                    <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => toggleLogEntry(entry.id)}>{entry.expanded ? '−' : '+'}</button>
                      <div style={{ flex: 1, fontSize: 13, color: '#333' }}>{typeof entry.raw === 'string' ? entry.raw : (entry.raw && entry.raw.summary ? entry.raw.summary : '')}</div>
                      {entry.usage && (
                        <div style={{ marginLeft: 8, fontSize: 12, color: '#444' }}>
                          <div style={{ fontWeight: 700 }}>Tokens</div>
                          <div style={{ fontSize: 12 }}>
                            {entry.usage.total_tokens ? `total: ${entry.usage.total_tokens}` : `${entry.usage.prompt_tokens || 0}p / ${entry.usage.completion_tokens || 0}c`}
                          </div>
                        </div>
                      )}
                    </div>
                    {entry.expanded && (
                      <pre style={{ marginTop: 8, background: '#f8f9fa', padding: 8, borderRadius: 6, overflow: 'auto', maxHeight: 360 }}>{JSON.stringify(entry.raw, null, 2)}</pre>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                <button className="btn btn-secondary" onClick={() => setLogEntries([])}>Limpar</button>
                <button className="btn btn-primary" onClick={() => setShowLogModal(false)}>Fechar</button>
              </div>
            </div>
          </div>
        )}
    </>
  );
};

export default BenchmarkPerfisSAP;

// Simple BarList component (renders horizontal bars)
const BarList: React.FC<{ data: { label: string; count: number }[]; onBarClick: (label: string) => void }> = ({ data, onBarClick }) => {
  const max = data.length ? Math.max(...data.map(d => d.count)) : 0;
  return (
    <div>
      {data.map((d, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <div className="bar-label" style={{ width: 220, fontSize: 13 }}>{d.label}</div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, background: '#f1f3f5', borderRadius: 6, height: 28, position: 'relative', cursor: 'pointer' }} onClick={() => onBarClick(d.label)}>
              <div style={{ width: `${max ? Math.round((d.count / max) * 100) : 0}%`, height: '100%', background: 'linear-gradient(90deg,#4a90e2,#0d6efd)', borderRadius: 6 }} />
            </div>
            <div className="bar-count" style={{ width: 48, textAlign: 'right', fontSize: 13, fontWeight: 700 }}>{d.count}</div>
          </div>
        </div>
      ))}
      {data.length === 0 && (<div style={{ color: '#666' }}>Nenhum dado disponível</div>)}
    </div>
  );
};
